import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, Crown, Rocket, TrendingUp, ShieldCheck, Building2, User, Users,
  CalendarCheck, MessageSquareWarning, ClipboardList, Utensils, Sparkles, ArrowRight,
} from "lucide-react";
import { Logo, Btn } from "@/components/kit";
import { PLANS, INCLUDED, VALUE_PROPS, FOUNDING, FOUNDING_NOTE, TRIAL_NOTE } from "@/lib/plans";
import { FAQ_SECTIONS, HOSPITALITY_TYPES, EXPERIENCES } from "@/lib/content";
import { LEGAL_DOCS } from "@/lib/legal";

const PLAN_ICON = { launch: Rocket, elevate: TrendingUp, signature: Crown };

const PORTALS = [
  { icon: User, label: "Guest", grad: "from-[#FF5E36] to-[#FF2A6D]", desc: "Book services and raise service requests.", to: "/login?portal=guest" },
  { icon: Users, label: "Staff · My Department", grad: "from-[#A855F7] to-[#7F00FF]", desc: "Department tasks, allergy alerts, handovers.", to: "/login?portal=staff" },
  { icon: Building2, label: "Reception", grad: "from-[#00D2FF] to-[#0078FF]", desc: "Booking lifecycle and complaint triage.", to: "/login?portal=reception" },
  { icon: ShieldCheck, label: "Management", grad: "from-[#F6B73C] to-[#FF6B35]", desc: "Cross-location oversight and analytics.", to: "/login?portal=admin" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="bd-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-[#2E1256] bg-[#0B031E]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-purple-200">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <Link to="/about" data-testid="nav-about" className="hover:text-white transition-colors">About</Link>
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="nav-signin" onClick={() => navigate("/login")} className="text-sm font-semibold text-purple-200 hover:text-white transition-colors">Sign in</button>
            <Btn data-testid="nav-trial" onClick={() => navigate("/business/register")}>Start free trial</Btn>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#451B7F] bg-[#180735]/60 text-xs font-semibold text-purple-200 mb-8">
          <Sparkles size={14} className="text-[#FF6B35]" /> Smarter. Connected. Elevated.
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-4xl mx-auto">
          Prevent operational failures <span className="bd-grad-text">before they become</span> negative guest experiences.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
          className="mt-6 text-base sm:text-lg text-purple-200/70 max-w-2xl mx-auto leading-relaxed">
          BeetDrop is a hospitality operations platform that centralises communication, reservations, guest preferences and complaint resolution — so issues reach the right people and get handled properly.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Btn className="!px-7 !py-3.5 text-base" data-testid="hero-trial" onClick={() => navigate("/business/register")}>Start your 30-day free trial <ArrowRight size={18} /></Btn>
          <Btn variant="outline" className="!px-7 !py-3.5 text-base" data-testid="hero-signin" onClick={() => navigate("/login")}>Sign in</Btn>
        </motion.div>
        <p className="mt-4 text-xs text-purple-400/60">No card required · No setup fees · No long-term contract</p>
      </section>

      {/* Two experiences */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-white text-center">{EXPERIENCES.heading}</h2>
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <div className="rounded-2xl bg-[#180735] border border-[#2E1256] p-7">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00D2FF] to-[#0078FF] flex items-center justify-center mb-4"><Building2 size={22} className="text-white" /></div>
            <h3 className="font-head text-lg font-bold text-white">{EXPERIENCES.business.title}</h3>
            <p className="text-sm text-purple-300/70 mt-2 leading-relaxed">{EXPERIENCES.business.body}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Btn data-testid="exp-biz-register" onClick={() => navigate("/business/register")}>Register your business</Btn>
              <Btn variant="outline" data-testid="exp-biz-signin" onClick={() => navigate("/login")}>Business sign in</Btn>
            </div>
          </div>
          <div className="rounded-2xl bg-[#180735] border border-[#2E1256] p-7">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF5E36] to-[#FF2A6D] flex items-center justify-center mb-4"><User size={22} className="text-white" /></div>
            <h3 className="font-head text-lg font-bold text-white">{EXPERIENCES.guest.title}</h3>
            <p className="text-sm text-purple-300/70 mt-2 leading-relaxed">{EXPERIENCES.guest.body}</p>
            <div className="flex flex-wrap gap-2 mt-5">
              <Btn data-testid="exp-guest-access" onClick={() => navigate("/login?portal=guest")}>Guest access</Btn>
            </div>
          </div>
        </div>
        <p className="text-sm text-purple-300/70 text-center mt-6 max-w-2xl mx-auto">{EXPERIENCES.tagline}</p>
      </section>

      {/* Hospitality business types */}
      <section className="max-w-5xl mx-auto px-6 pb-2">
        <div className="text-center mb-5"><span className="text-xs font-semibold tracking-widest uppercase text-[#FF6B35]">For hospitality businesses</span></div>
        <div className="flex flex-wrap justify-center gap-2">
          {HOSPITALITY_TYPES.map((t) => (
            <span key={t} className="px-3 py-1.5 rounded-full bg-[#180735] border border-[#2E1256] text-sm text-purple-100">{t}</span>
          ))}
        </div>
      </section>

      {/* Included in every plan */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <span className="text-xs font-semibold tracking-widest uppercase text-[#FF6B35]">Included in every plan</span>
          <h2 className="font-head text-2xl sm:text-3xl font-bold text-white mt-2">Everything you need to run great operations</h2>
          <p className="text-sm text-purple-300/70 mt-3 max-w-2xl mx-auto">No business is punished for receiving more reservations or handling more guest issues. Plans scale on business size and operational complexity — not on success.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {INCLUDED.map((f, i) => (
            <motion.div key={f} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="flex items-center gap-2.5 rounded-xl bg-[#180735] border border-[#2E1256] px-4 py-3.5">
              <span className="w-6 h-6 rounded-full bg-[#FF2A6D]/15 flex items-center justify-center shrink-0"><Check size={14} className="text-[#FF6B7D]" /></span>
              <span className="text-sm text-purple-100 leading-tight">{f}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="font-head text-3xl sm:text-4xl font-bold text-white">Choose the plan that fits your business</h2>
          <p className="text-sm text-purple-300/70 mt-3">A predictable monthly subscription that grows alongside you.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {PLANS.map((p, i) => {
            const Icon = PLAN_ICON[p.id];
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`plan-card-${p.id}`}
                className={`relative rounded-2xl bg-[#180735] border ${p.popular ? "border-[#A855F7]/60 shadow-[0_0_40px_rgba(168,85,247,0.15)] lg:-mt-4 lg:mb-4" : p.ring} p-7 overflow-hidden`}>
                {p.popular && <span className="absolute top-0 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-wider uppercase px-4 py-1 rounded-b-lg text-white bg-gradient-to-r from-[#A855F7] to-[#FF2A6D]">★ Most Popular</span>}
                <div className={`absolute -right-8 -top-8 w-28 h-28 rounded-full blur-3xl opacity-20 ${p.glow}`} />
                <div className="flex items-center gap-3 mb-2 mt-2">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.accent} flex items-center justify-center`}><Icon size={22} className="text-[#0B031E]" /></div>
                  <h3 className="font-head text-2xl font-extrabold text-white">{p.name}</h3>
                </div>
                <p className="text-sm text-purple-300/70 min-h-[40px]">{p.tagline}</p>
                <p className="text-xs text-purple-400/60 mt-1 min-h-[32px]">{p.audience}</p>
                <div className="mt-5 mb-1 flex items-end gap-1">
                  <span className="font-head text-4xl font-extrabold text-white">{p.price}</span>
                  <span className="text-sm text-purple-300/70 mb-1">{p.period}</span>
                </div>
                <p className="text-xs text-purple-300/70 mb-4">Annual: {p.annual} <span className="text-emerald-400">· {p.annualNote}</span></p>
                <Btn variant={p.popular ? "primary" : "secondary"} className="w-full" data-testid={`plan-cta-${p.id}`} onClick={() => navigate(`/business/register?plan=${p.id}`)}>
                  Start free trial
                </Btn>
                {p.plus && <p className="text-xs font-semibold text-[#FF6B7D] mt-6">{p.plus}</p>}
                <div className={`space-y-5 ${p.plus ? "mt-3" : "mt-6"}`}>
                  {p.groups.map((g) => (
                    <div key={g.title}>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-300/50 mb-2">{g.title}</div>
                      <ul className="space-y-1.5">
                        {g.items.map((it) => (
                          <li key={it} className="flex items-start gap-2 text-sm text-purple-100/90"><Check size={15} className="text-emerald-400 mt-0.5 shrink-0" /> {it}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-purple-400/60 mt-6 pt-4 border-t border-[#2E1256] italic">{p.best}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Founding Customer Programme */}
        <div className="mt-10 rounded-2xl bg-[#140530] border border-[#2E1256] p-6 sm:p-8">
          <h3 className="font-head text-xl font-bold text-white">Founding Customer Programme</h3>
          <p className="text-sm text-purple-300/70 mt-1">Separate introductory pricing for early businesses.</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-5">
            {FOUNDING.map((f) => (
              <div key={f.name} className="rounded-xl bg-[#1B0838] border border-[#2E1256] p-4 text-center">
                <div className="text-sm font-semibold text-white">{f.name}</div>
                <div className="font-head text-lg font-extrabold text-amber-300 mt-1">{f.price}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-purple-400/70 mt-4 italic">{FOUNDING_NOTE}</p>
        </div>
      </section>

      {/* Free trial banner */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="rounded-2xl bg-gradient-to-r from-[#FF5E36]/15 via-[#FF2A6D]/10 to-[#A855F7]/15 border border-[#FF2A6D]/30 p-8 text-center">
          <h3 className="font-head text-2xl font-bold text-white">30-Day Free Trial</h3>
          <p className="text-sm text-purple-100 mt-2 max-w-2xl mx-auto leading-relaxed">{TRIAL_NOTE}</p>
          <Btn className="mt-6" data-testid="trial-cta" onClick={() => navigate("/business/register")}>Create your business account</Btn>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <span className="text-xs font-semibold tracking-widest uppercase text-[#FF6B35]">FAQ</span>
          <h2 className="font-head text-2xl sm:text-3xl font-bold text-white mt-2">Questions, by audience</h2>
        </div>
        <div className="space-y-10">
          {FAQ_SECTIONS.map((sec) => (
            <div key={sec.title}>
              <h3 className="font-head text-lg font-bold text-white mb-4 flex items-center gap-2"><span className="w-1.5 h-5 rounded-full bg-gradient-to-b from-[#FF5E36] to-[#FF2A6D]" /> {sec.title}</h3>
              <div className="space-y-3">
                {sec.items.map((qa) => (
                  <div key={qa.q} className="rounded-xl bg-[#180735] border border-[#2E1256] p-5">
                    <div className="font-semibold text-white">{qa.q}</div>
                    <p className="text-sm text-purple-200/80 mt-2 leading-relaxed">{qa.a}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Value proposition */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="rounded-2xl bg-[#140530] border border-[#2E1256] p-8 sm:p-10">
          <h2 className="font-head text-2xl font-bold text-white mb-6">The BeetDrop value proposition</h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {VALUE_PROPS.map((v) => (
              <div key={v} className="flex items-start gap-3 text-sm text-purple-100/90">
                <span className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5"><Check size={13} className="text-emerald-400" /></span>{v}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#2E1256] mt-10">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Logo size={32} />
              <span className="text-sm text-purple-400/70 hidden sm:inline">Better Operations. Happier Guests. Stronger Businesses.</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" data-testid="footer-about" className="text-purple-200 hover:text-white transition-colors">About</Link>
              <Link to="/login" className="text-purple-200 hover:text-white transition-colors">Sign in</Link>
              <Link to="/business/register" className="text-purple-200 hover:text-white transition-colors">Start free trial</Link>
              <span className="font-head font-bold bd-grad-text">BEETDROP.APP</span>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#2E1256] flex flex-wrap gap-x-6 gap-y-2">
            {LEGAL_DOCS.map((d) => (
              <Link key={d.slug} to={`/legal/${d.slug}`} data-testid={`footer-legal-${d.slug}`} className="text-xs text-purple-400/70 hover:text-white transition-colors">{d.title}</Link>
            ))}
          </div>
          <p className="mt-4 text-xs text-purple-500/60">© 2026 BeetDrop. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
